import { useState, useEffect, useRef } from "react";
import { useAuth, api } from "../App";
import { useClinicSettings } from "../hooks/useClinicSettings";
import { useTheme } from "../hooks/useTheme";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { PasswordDialog } from "./PasswordDialog";
import {
  User, Mail, Shield, Bell, Moon, Sun, Palette, Clock,
  Save, Loader2, Check, ChevronRight, Upload, Trash2, ImageIcon, PenTool, Pill, Plus, Edit, X,
  LayoutDashboard, DollarSign, BarChart3
} from "lucide-react";
import SignatureCanvas from "./SignatureCanvas";

const SECTIONS = [
  { id: "profile", label: "Perfil", icon: User },
  { id: "permissions", label: "Permissões", icon: Shield },
  { id: "signature", label: "Assinatura", icon: PenTool },
  { id: "notifications", label: "Notificações", icon: Bell },
  { id: "appearance", label: "Aparência", icon: Palette },
  { id: "clinic", label: "Clínica", icon: Shield },
  { id: "medications", label: "Receituário", icon: Pill },
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SettingsModal({ open, onClose }) {
  const { user, setUserData } = useAuth();
  const { settings: clinicCtx, updateSettings: updateClinicCtx, fetchSettings: fetchClinicCtx } = useClinicSettings();
  const { theme, setLightTheme, setDarkTheme, isDark } = useTheme();
  const [section, setSection] = useState("profile");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [signatureData, setSignatureData] = useState(null);
  const [savingSignature, setSavingSignature] = useState(false);
  const logoInputRef = useRef(null);

  const [profileData, setProfileData] = useState({ name: "", email: "", phone: "", cro: "" });
  
  const [permissions, setPermissions] = useState({
    access_dashboard: true,
    view_financial_cards: true,
    access_reports: true
  });
  
  // Estados para proteção por senha na aba Permissões
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [permissionsUnlocked, setPermissionsUnlocked] = useState(false);
  
  // Estados para alterar senha admin
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current: "", new: "", confirm: "" });

  const [notifSettings, setNotifSettings] = useState({
    emailReminders: true,
    browserNotifs: true,
    leadTime: 30,
    soundEnabled: true,
  });

  const [clinicData, setClinicData] = useState({
    clinicName: "ItapeviDents",
    clinicLogo: null,
    openTime: "08:00",
    closeTime: "18:00",
    slotDuration: 30,
  });

  const [medications, setMedications] = useState([]);
  const [editingMed, setEditingMed] = useState(null);
  const [medForm, setMedForm] = useState({ name: "", dosage: "", instructions: "" });
  const [savingMed, setSavingMed] = useState(false);

  useEffect(() => {
    if (open && user) {
      setProfileData({ name: user.name || "", email: user.email || "", phone: user.phone || "", cro: user.cro || "" });
      setPermissions(user.permissions || { access_dashboard: true, view_financial_cards: true });
      setSaved(false);
      fetchNotifSettings();
      fetchClinicSettings();
      fetchSignature();
      if (section === "medications") {
        fetchMedications();
      }
    }
  }, [open, user, section]);

  const fetchSignature = async () => {
    try {
      const res = await api.get("/signatures");
      setSignatureData(res.data?.signature_data || null);
    } catch {}
  };

  const handleSaveSignature = async (data) => {
    setSavingSignature(true);
    try {
      await api.post("/signatures", { signature_data: data });
      setSignatureData(data);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error("Save signature error:", err);
    } finally {
      setSavingSignature(false);
    }
  };

  const handleDeleteSignature = async () => {
    try {
      await api.delete("/signatures");
      setSignatureData(null);
    } catch (err) {
      console.error("Delete signature error:", err);
    }
  };

  const handleSavePermissions = async () => {
    setSaving(true);
    try {
      const response = await api.put("/auth/users/permissions", { permissions });
      
      // Atualizar usuário no contexto E no localStorage
      if (response.data && response.data.permissions) {
        const updatedUser = { ...user, permissions: response.data.permissions };
        
        // Atualizar contexto
        setUserData(updatedUser);
        
        // CRÍTICO: Atualizar localStorage manualmente para garantir persistência
        localStorage.setItem("user", JSON.stringify(updatedUser));
        
        console.log("Permissões salvas:", response.data.permissions);
        console.log("LocalStorage atualizado:", localStorage.getItem("user"));
      }
      
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error("Save permissions error:", err);
      alert("Erro ao salvar permissões");
    } finally {
      setSaving(false);
    }
  };
  
  // Handler para quando senha for validada com sucesso
  const handlePasswordSuccess = () => {
    setPermissionsUnlocked(true);
    setShowPasswordDialog(false);
    setSection("permissions"); // Mudar para a seção após validar senha
  };
  
  // Handler para alterar senha admin
  const handleChangeAdminPassword = async () => {
    if (passwordForm.new !== passwordForm.confirm) {
      alert("A nova senha e a confirmação não coincidem");
      return;
    }
    
    if (passwordForm.new.length < 4) {
      alert("A nova senha deve ter pelo menos 4 caracteres");
      return;
    }
    
    setSaving(true);
    try {
      await api.put("/auth/users/admin-password", {
        current_password: passwordForm.current,
        new_password: passwordForm.new
      });
      
      alert("Senha admin alterada com sucesso!");
      setPasswordForm({ current: "", new: "", confirm: "" });
      setShowChangePassword(false);
    } catch (err) {
      console.error("Change password error:", err);
      if (err.response?.status === 403) {
        alert("Senha atual incorreta");
      } else {
        alert("Erro ao alterar senha");
      }
    } finally {
      setSaving(false);
    }
  };

  // Funções para gerenciar medicamentos
  const fetchMedications = async () => {
    try {
      const res = await api.get("/medications");
      setMedications(res.data.medications || []);
    } catch (err) {
      console.error("Fetch medications error:", err);
    }
  };

  const handleSaveMedication = async () => {
    if (!medForm.name.trim()) {
      alert("Nome do medicamento é obrigatório");
      return;
    }

    setSavingMed(true);
    try {
      if (editingMed) {
        // Editar
        await api.put(`/medications/${editingMed.medication_id}`, medForm);
      } else {
        // Criar novo
        await api.post("/medications", medForm);
      }
      
      await fetchMedications();
      setMedForm({ name: "", dosage: "", instructions: "" });
      setEditingMed(null);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error("Save medication error:", err);
      alert("Erro ao salvar medicamento");
    } finally {
      setSavingMed(false);
    }
  };

  const handleEditMedication = (med) => {
    setEditingMed(med);
    setMedForm({
      name: med.name || "",
      dosage: med.dosage || "",
      instructions: med.instructions || ""
    });
  };

  const handleDeleteMedication = async (medId) => {
    if (!window.confirm("Deseja realmente excluir este medicamento?")) return;
    
    try {
      await api.delete(`/medications/${medId}`);
      await fetchMedications();
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error("Delete medication error:", err);
      alert("Erro ao excluir medicamento");
    }
  };

  const handleCancelEdit = () => {
    setEditingMed(null);
    setMedForm({ name: "", dosage: "", instructions: "" });
  };


  const fetchNotifSettings = async () => {
    try {
      const res = await api.get("/notifications/settings");
      if (res.data) {
        setNotifSettings(prev => ({
          ...prev,
          browserNotifs: res.data.enabled ?? true,
          leadTime: res.data.lead_time_minutes ?? 30,
          soundEnabled: res.data.sound_enabled ?? true,
        }));
      }
    } catch {}
  };

  const fetchClinicSettings = async () => {
    try {
      const res = await api.get("/auth/clinic-settings");
      if (res.data) {
        setClinicData({
          clinicName: res.data.clinic_name || "ItapeviDents",
          clinicLogo: res.data.clinic_logo || null,
          openTime: res.data.open_time || "08:00",
          closeTime: res.data.close_time || "18:00",
          slotDuration: res.data.slot_duration || 30,
        });
      }
    } catch {}
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (section === "profile") {
        const res = await api.put("/auth/profile", profileData);
        if (res.data?.user) {
          setUserData(res.data.user);
        }
      }
      if (section === "notifications") {
        await api.put("/notifications/settings", {
          enabled: notifSettings.browserNotifs,
          lead_time_minutes: notifSettings.leadTime,
          sound_enabled: notifSettings.soundEnabled,
        });
      }
      if (section === "clinic") {
        const res = await api.put("/auth/clinic-settings", {
          clinic_name: clinicData.clinicName,
          open_time: clinicData.openTime,
          close_time: clinicData.closeTime,
          slot_duration: clinicData.slotDuration,
        });
        if (res.data) {
          updateClinicCtx(res.data);
        }
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error("Save error:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await api.post("/auth/clinic-logo", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data?.logo_url) {
        setClinicData(prev => ({ ...prev, clinicLogo: res.data.logo_url }));
        updateClinicCtx({ clinic_logo: res.data.logo_url });
      }
    } catch (err) {
      console.error("Logo upload error:", err);
    } finally {
      setUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = "";
    }
  };

  const handleDeleteLogo = async () => {
    try {
      await api.delete("/auth/clinic-logo");
      setClinicData(prev => ({ ...prev, clinicLogo: null }));
      updateClinicCtx({ clinic_logo: null });
    } catch (err) {
      console.error("Delete logo error:", err);
    }
  };

  const logoSrc = clinicData.clinicLogo ? `${BACKEND_URL}${clinicData.clinicLogo}` : null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-2xl p-0 overflow-hidden max-h-[85vh]" aria-describedby="settings-desc">
        <DialogHeader className="sr-only">
          <DialogTitle>Configurações</DialogTitle>
        </DialogHeader>
        <p id="settings-desc" className="sr-only">Configurações do sistema</p>

        <div className="flex h-[520px]">
          {/* Sidebar */}
          <div className="w-52 bg-slate-50 border-r border-slate-200 p-3 flex flex-col">
            <p className="text-lg font-bold text-slate-900 px-3 pt-2 pb-4">Configurações</p>
            <nav className="space-y-0.5 flex-1">
              {SECTIONS.map((s) => {
                const Icon = s.icon;
                const active = section === s.id;
                return (
                  <button
                    key={s.id}
                    onClick={() => {
                      // Se for seção Permissões, pedir senha primeiro
                      if (s.id === "permissions" && !permissionsUnlocked) {
                        setShowPasswordDialog(true);
                      } else {
                        setSection(s.id);
                        setSaved(false);
                      }
                    }}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-all ${
                      active
                        ? "bg-white shadow-sm text-teal-700 font-medium border border-slate-200"
                        : "text-slate-600 hover:bg-white/60 hover:text-slate-900"
                    }`}
                    data-testid={`settings-nav-${s.id}`}
                  >
                    <Icon className="w-4 h-4" />
                    {s.label}
                    {active && <ChevronRight className="w-3.5 h-3.5 ml-auto text-slate-400" />}
                  </button>
                );
              })}
            </nav>
            <div className="px-3 pt-3 border-t border-slate-200">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center">
                  <span className="text-sm font-medium text-teal-700">{user?.name?.charAt(0) || "U"}</span>
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-slate-800 truncate">{user?.name}</p>
                  <p className="text-[10px] text-slate-400 truncate">{user?.email}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 flex flex-col">
            <div className="flex-1 p-6 overflow-y-auto">
              {/* Profile */}
              {section === "profile" && (
                <div className="space-y-5">
                  <div>
                    <h3 className="text-base font-semibold text-slate-900">Perfil</h3>
                    <p className="text-sm text-slate-500">Informações pessoais da sua conta</p>
                  </div>
                  <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-teal-500 to-emerald-500 flex items-center justify-center">
                      <span className="text-2xl font-bold text-white">{profileData.name?.charAt(0) || "U"}</span>
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">{profileData.name || "Usuário"}</p>
                      <p className="text-sm text-slate-500">{profileData.email}</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs text-slate-500">Nome</Label>
                      <Input
                        value={profileData.name}
                        onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                        data-testid="settings-name"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-slate-500">Email</Label>
                      <Input
                        value={profileData.email}
                        disabled
                        className="bg-slate-50"
                        data-testid="settings-email"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-slate-500">Telefone</Label>
                      <Input
                        value={profileData.phone}
                        onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                        placeholder="(11) 99999-9999"
                        data-testid="settings-phone"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-slate-500">CRO (Conselho Regional de Odontologia)</Label>
                      <Input
                        value={profileData.cro}
                        onChange={(e) => setProfileData({ ...profileData, cro: e.target.value })}
                        placeholder="CRO-SP 12345"
                        data-testid="settings-cro"
                      />
                      <p className="text-xs text-slate-400">Ex: CRO-SP 12345 ou CRO-RJ 67890</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Permissions */}
              {section === "permissions" && (
                <div className="space-y-5">
                  <div>
                    <h3 className="text-base font-semibold text-slate-900">Permissões de Acesso</h3>
                    <p className="text-sm text-slate-500">Controle quais páginas e recursos você pode visualizar</p>
                  </div>
                  
                  <div className="space-y-3">
                    {/* Acesso ao Painel */}
                    <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-slate-200">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-teal-50 flex items-center justify-center">
                          <LayoutDashboard className="w-4 h-4 text-teal-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-800">Acesso ao Painel</p>
                          <p className="text-xs text-slate-500">Visualizar página de dashboard com estatísticas</p>
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={permissions.access_dashboard}
                          onChange={(e) => setPermissions({ ...permissions, access_dashboard: e.target.checked })}
                          className="sr-only peer"
                          data-testid="permission-dashboard"
                        />
                        <div className="w-11 h-6 bg-slate-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-600"></div>
                      </label>
                    </div>

                    {/* Visualizar Cards Financeiros */}
                    <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-slate-200">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center">
                          <DollarSign className="w-4 h-4 text-emerald-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-800">Ver Cards Financeiros</p>
                          <p className="text-xs text-slate-500">Visualizar resumo financeiro (Recebido, Pendente, Total)</p>
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={permissions.view_financial_cards}
                          onChange={(e) => setPermissions({ ...permissions, view_financial_cards: e.target.checked })}
                          className="sr-only peer"
                          data-testid="permission-financial-cards"
                        />
                        <div className="w-11 h-6 bg-slate-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
                      </label>
                    </div>

                    {/* Acesso aos Relatórios */}
                    <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-slate-200">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-purple-50 flex items-center justify-center">
                          <BarChart3 className="w-4 h-4 text-purple-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-800">Acesso aos Relatórios</p>
                          <p className="text-xs text-slate-500">Visualizar página de relatórios e estatísticas</p>
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={permissions.access_reports}
                          onChange={(e) => setPermissions({ ...permissions, access_reports: e.target.checked })}
                          className="sr-only peer"
                          data-testid="permission-reports"
                        />
                        <div className="w-11 h-6 bg-slate-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                      </label>
                    </div>
                  </div>

                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <p className="text-[11px] text-blue-800 leading-relaxed">
                      <strong>Nota:</strong> As permissões controlam o acesso às funcionalidades do sistema. Desabilitar uma permissão ocultará o menu e impedirá o acesso à página correspondente.
                    </p>
                  </div>
                  
                  {/* Alterar Senha Admin */}
                  <div className="border-t border-slate-200 pt-5">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h4 className="text-sm font-semibold text-slate-900">Senha de Administrador</h4>
                        <p className="text-xs text-slate-500">Altere a senha usada para acessar esta seção</p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowChangePassword(!showChangePassword)}
                        data-testid="toggle-change-password"
                      >
                        {showChangePassword ? "Cancelar" : "Alterar Senha"}
                      </Button>
                    </div>
                    
                    {showChangePassword && (
                      <div className="space-y-3 mt-3 p-4 bg-slate-50 rounded-lg">
                        <div>
                          <Label htmlFor="current-password" className="text-xs">Senha Atual</Label>
                          <Input
                            id="current-password"
                            type="password"
                            value={passwordForm.current}
                            onChange={(e) => setPasswordForm({ ...passwordForm, current: e.target.value })}
                            placeholder="Digite a senha atual"
                            className="text-sm"
                          />
                        </div>
                        <div>
                          <Label htmlFor="new-password" className="text-xs">Nova Senha</Label>
                          <Input
                            id="new-password"
                            type="password"
                            value={passwordForm.new}
                            onChange={(e) => setPasswordForm({ ...passwordForm, new: e.target.value })}
                            placeholder="Digite a nova senha"
                            className="text-sm"
                          />
                        </div>
                        <div>
                          <Label htmlFor="confirm-password" className="text-xs">Confirmar Nova Senha</Label>
                          <Input
                            id="confirm-password"
                            type="password"
                            value={passwordForm.confirm}
                            onChange={(e) => setPasswordForm({ ...passwordForm, confirm: e.target.value })}
                            placeholder="Digite novamente a nova senha"
                            className="text-sm"
                          />
                        </div>
                        <Button
                          onClick={handleChangeAdminPassword}
                          disabled={saving || !passwordForm.current || !passwordForm.new || !passwordForm.confirm}
                          className="w-full bg-amber-600 hover:bg-amber-700 text-sm"
                          size="sm"
                        >
                          {saving ? "Salvando..." : "Confirmar Alteração"}
                        </Button>
                      </div>
                    )}
                  </div>

                  <Button
                    onClick={handleSavePermissions}
                    disabled={saving}
                    className="w-full bg-teal-600 hover:bg-teal-700"
                    data-testid="save-permissions-btn"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Salvando...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Salvar Permissões
                      </>
                    )}
                  </Button>
                </div>
              )}

              {/* Signature */}
              {section === "signature" && (
                <div className="space-y-5">
                  <div>
                    <h3 className="text-base font-semibold text-slate-900">Assinatura Eletrônica</h3>
                    <p className="text-sm text-slate-500">Cadastre sua assinatura para uso em prontuários e documentos</p>
                  </div>
                  
                  {signatureData ? (
                    <div className="space-y-3">
                      <div className="p-4 bg-white rounded-xl border border-slate-200">
                        <Label className="text-xs text-slate-500 mb-2 block">Assinatura atual</Label>
                        <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                          <img
                            src={signatureData}
                            alt="Assinatura"
                            className="max-h-24 mx-auto"
                            data-testid="signature-preview"
                          />
                        </div>
                        <div className="flex items-center gap-2 mt-3">
                          <div className="w-2 h-2 rounded-full bg-emerald-500" />
                          <span className="text-xs text-emerald-600 font-medium">Assinatura cadastrada</span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleDeleteSignature}
                          className="text-red-600 border-red-200 hover:bg-red-50"
                          data-testid="delete-signature-btn"
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-1" /> Remover assinatura
                        </Button>
                      </div>
                      <div className="pt-3 border-t border-slate-100">
                        <Label className="text-xs text-slate-500 mb-2 block">Atualizar assinatura</Label>
                        <SignatureCanvas onSave={handleSaveSignature} saving={savingSignature} />
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                        <p className="text-sm text-amber-800">Você ainda não cadastrou sua assinatura. Desenhe abaixo para cadastrar.</p>
                      </div>
                      <SignatureCanvas onSave={handleSaveSignature} saving={savingSignature} />
                    </div>
                  )}

                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                    <p className="text-[10px] text-slate-500 leading-relaxed">
                      <strong>Nota:</strong> Esta assinatura eletrônica simples será utilizada para assinar prontuários e documentos clínicos.
                      Cada assinatura gera um hash SHA-256 único para garantir a integridade do documento, e todas as ações são registradas no log de auditoria.
                    </p>
                  </div>
                </div>
              )}

              {/* Notifications */}
              {section === "notifications" && (
                <div className="space-y-5">
                  <div>
                    <h3 className="text-base font-semibold text-slate-900">Notificações</h3>
                    <p className="text-sm text-slate-500">Gerencie como você recebe alertas</p>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-slate-200">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
                          <Bell className="w-4 h-4 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-800">Notificações do navegador</p>
                          <p className="text-xs text-slate-500">Alertas de consultas próximas</p>
                        </div>
                      </div>
                      <Switch
                        checked={notifSettings.browserNotifs}
                        onCheckedChange={(v) => setNotifSettings({ ...notifSettings, browserNotifs: v })}
                        data-testid="settings-browser-notif"
                      />
                    </div>
                    <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-slate-200">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-purple-50 flex items-center justify-center">
                          <Mail className="w-4 h-4 text-purple-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-800">Lembretes por email</p>
                          <p className="text-xs text-slate-500">Receber lembretes de consulta por email</p>
                        </div>
                      </div>
                      <Switch
                        checked={notifSettings.emailReminders}
                        onCheckedChange={(v) => setNotifSettings({ ...notifSettings, emailReminders: v })}
                        data-testid="settings-email-reminders"
                      />
                    </div>
                    <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-slate-200">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center">
                          <Clock className="w-4 h-4 text-amber-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-800">Antecedência do alerta</p>
                          <p className="text-xs text-slate-500">Minutos antes da consulta</p>
                        </div>
                      </div>
                      <select
                        value={notifSettings.leadTime}
                        onChange={(e) => setNotifSettings({ ...notifSettings, leadTime: parseInt(e.target.value) })}
                        className="h-9 rounded-md border border-input bg-background px-3 text-sm"
                        data-testid="settings-lead-time"
                      >
                        <option value={10}>10 min</option>
                        <option value={15}>15 min</option>
                        <option value={30}>30 min</option>
                        <option value={60}>1 hora</option>
                      </select>
                    </div>
                    <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-slate-200">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-teal-50 flex items-center justify-center">
                          <Bell className="w-4 h-4 text-teal-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-800">Som de notificação</p>
                          <p className="text-xs text-slate-500">Reproduzir som ao receber alerta</p>
                        </div>
                      </div>
                      <Switch
                        checked={notifSettings.soundEnabled}
                        onCheckedChange={(v) => setNotifSettings({ ...notifSettings, soundEnabled: v })}
                        data-testid="settings-sound"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Appearance */}
              {section === "appearance" && (
                <div className="space-y-5">
                  <div>
                    <h3 className="text-base font-semibold text-slate-900">Aparência</h3>
                    <p className="text-sm text-slate-500">Personalize a interface do sistema</p>
                  </div>
                  <div className="space-y-3">
                    <div className="p-4 bg-white rounded-xl border border-slate-200">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-9 h-9 rounded-lg bg-teal-50 flex items-center justify-center">
                          <Palette className="w-4 h-4 text-teal-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-800">Cor principal</p>
                          <p className="text-xs text-slate-500">Cor de destaque do sistema</p>
                        </div>
                      </div>
                      <div className="flex gap-2 ml-12">
                        {[
                          { name: "Teal", class: "bg-teal-500", active: true },
                          { name: "Blue", class: "bg-blue-500" },
                          { name: "Purple", class: "bg-purple-500" },
                          { name: "Rose", class: "bg-rose-500" },
                          { name: "Amber", class: "bg-amber-500" },
                        ].map((c) => (
                          <button
                            key={c.name}
                            className={`w-8 h-8 rounded-full ${c.class} transition-transform ${c.active ? "ring-2 ring-offset-2 ring-teal-500 scale-110" : "hover:scale-110"}`}
                            title={c.name}
                          />
                        ))}
                      </div>
                    </div>
                    <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                      <div className="flex items-center gap-3">
                        <Moon className="w-5 h-5 text-blue-600" />
                        <div>
                          <p className="text-sm font-medium text-blue-900">Menu lateral escuro ativo</p>
                          <p className="text-xs text-blue-700">O menu lateral permanece com fundo escuro para melhor contraste</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Clinic */}
              {section === "clinic" && (
                <div className="space-y-5">
                  <div>
                    <h3 className="text-base font-semibold text-slate-900">Clínica</h3>
                    <p className="text-sm text-slate-500">Configurações gerais da clínica</p>
                  </div>

                  {/* Logo upload */}
                  <div className="p-4 bg-white rounded-xl border border-slate-200">
                    <Label className="text-xs text-slate-500 mb-3 block">Logo da clínica</Label>
                    <div className="flex items-center gap-4">
                      <div className="w-20 h-20 rounded-xl border-2 border-dashed border-slate-200 flex items-center justify-center overflow-hidden bg-slate-50 flex-shrink-0">
                        {logoSrc ? (
                          <img
                            src={logoSrc}
                            alt="Logo"
                            className="w-full h-full object-contain"
                            data-testid="clinic-logo-preview"
                          />
                        ) : (
                          <ImageIcon className="w-8 h-8 text-slate-300" />
                        )}
                      </div>
                      <div className="flex flex-col gap-2">
                        <p className="text-xs text-slate-500">
                          {logoSrc ? "Logo carregado" : "PNG, JPG, SVG ou WEBP"}
                        </p>
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => logoInputRef.current?.click()}
                            disabled={uploadingLogo}
                            data-testid="upload-logo-btn"
                          >
                            {uploadingLogo ? (
                              <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                            ) : (
                              <Upload className="w-3.5 h-3.5 mr-1" />
                            )}
                            {uploadingLogo ? "Enviando..." : "Enviar logo"}
                          </Button>
                          {logoSrc && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={handleDeleteLogo}
                              className="text-red-600 border-red-200 hover:bg-red-50"
                              data-testid="delete-logo-btn"
                            >
                              <Trash2 className="w-3.5 h-3.5 mr-1" />
                              Remover
                            </Button>
                          )}
                        </div>
                        <input
                          ref={logoInputRef}
                          type="file"
                          className="hidden"
                          accept=".png,.jpg,.jpeg,.svg,.webp"
                          onChange={handleLogoUpload}
                          data-testid="logo-file-input"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs text-slate-500">Nome da clínica</Label>
                      <Input
                        value={clinicData.clinicName}
                        onChange={(e) => setClinicData({ ...clinicData, clinicName: e.target.value })}
                        data-testid="settings-clinic-name"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label className="text-xs text-slate-500">Abertura</Label>
                        <Input
                          type="time"
                          value={clinicData.openTime}
                          onChange={(e) => setClinicData({ ...clinicData, openTime: e.target.value })}
                          data-testid="settings-open-time"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-slate-500">Fechamento</Label>
                        <Input
                          type="time"
                          value={clinicData.closeTime}
                          onChange={(e) => setClinicData({ ...clinicData, closeTime: e.target.value })}
                          data-testid="settings-close-time"
                        />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-slate-500">Duração padrão da consulta</Label>
                      <select
                        value={clinicData.slotDuration}
                        onChange={(e) => setClinicData({ ...clinicData, slotDuration: parseInt(e.target.value) })}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        data-testid="settings-slot-duration"
                      >
                        <option value={15}>15 min</option>
                        <option value={30}>30 min</option>
                        <option value={45}>45 min</option>
                        <option value={60}>1 hora</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {section === "medications" && (
                <div className="space-y-6">
                  <div>
                    <h3 className="font-semibold text-base mb-1">Biblioteca de Medicamentos</h3>
                    <p className="text-sm text-slate-600">
                      Gerencie os medicamentos que aparecem no receituário
                    </p>
                  </div>

                  {/* Formulário de Adicionar/Editar */}
                  <div className="border rounded-lg p-4 space-y-4 bg-slate-50">
                    <h4 className="font-medium text-sm">
                      {editingMed ? "Editar Medicamento" : "Adicionar Novo Medicamento"}
                    </h4>
                    
                    <div className="space-y-3">
                      <div>
                        <Label className="text-xs text-slate-600">Nome do Medicamento *</Label>
                        <Input
                          value={medForm.name}
                          onChange={(e) => setMedForm({ ...medForm, name: e.target.value })}
                          placeholder="Ex: Amoxicilina"
                          className="mt-1"
                        />
                      </div>

                      <div>
                        <Label className="text-xs text-slate-600">Dosagem / Concentração</Label>
                        <Input
                          value={medForm.dosage}
                          onChange={(e) => setMedForm({ ...medForm, dosage: e.target.value })}
                          placeholder="Ex: 500mg"
                          className="mt-1"
                        />
                      </div>

                      <div>
                        <Label className="text-xs text-slate-600">Posologia Padrão</Label>
                        <Input
                          value={medForm.instructions}
                          onChange={(e) => setMedForm({ ...medForm, instructions: e.target.value })}
                          placeholder="Ex: 1 comprimido de 8 em 8 horas por 7 dias"
                          className="mt-1"
                        />
                      </div>

                      <div className="flex gap-2 pt-2">
                        <Button
                          onClick={handleSaveMedication}
                          disabled={savingMed}
                          size="sm"
                          className="flex-1"
                        >
                          {savingMed ? (
                            <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Salvando...</>
                          ) : editingMed ? (
                            <><Check className="w-3.5 h-3.5 mr-1" /> Atualizar</>
                          ) : (
                            <><Plus className="w-3.5 h-3.5 mr-1" /> Adicionar</>
                          )}
                        </Button>
                        {editingMed && (
                          <Button
                            onClick={handleCancelEdit}
                            variant="outline"
                            size="sm"
                          >
                            <X className="w-3.5 h-3.5 mr-1" /> Cancelar
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Lista de Medicamentos */}
                  <div className="space-y-2">
                    <h4 className="font-medium text-sm text-slate-700">
                      Medicamentos Cadastrados ({medications.length})
                    </h4>
                    
                    {medications.length === 0 ? (
                      <div className="text-center py-8 text-slate-500 text-sm border rounded-lg bg-slate-50">
                        Nenhum medicamento cadastrado ainda
                      </div>
                    ) : (
                      <div className="space-y-2 max-h-80 overflow-y-auto">
                        {medications.map((med) => (
                          <div
                            key={med.medication_id}
                            className="flex items-start justify-between p-3 border rounded-lg hover:bg-slate-50 transition-colors"
                          >
                            <div className="flex-1">
                              <p className="font-medium text-sm">{med.name}</p>
                              {med.dosage && (
                                <p className="text-xs text-slate-600 mt-0.5">
                                  Dosagem: {med.dosage}
                                </p>
                              )}
                              {med.instructions && (
                                <p className="text-xs text-slate-500 mt-1">
                                  {med.instructions}
                                </p>
                              )}
                            </div>
                            <div className="flex gap-1 ml-3">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditMedication(med)}
                                className="h-8 w-8 p-0"
                                title="Editar"
                              >
                                <Edit className="w-3.5 h-3.5 text-blue-600" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteMedication(med.medication_id)}
                                className="h-8 w-8 p-0"
                                title="Excluir"
                              >
                                <Trash2 className="w-3.5 h-3.5 text-red-600" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-3 border-t border-slate-200 bg-white flex items-center justify-end gap-2">
              <Button variant="outline" size="sm" onClick={onClose}>
                Fechar
              </Button>
              {section !== "signature" && section !== "medications" && (
                <Button
                  size="sm"
                  className="bg-teal-700 hover:bg-teal-800"
                  onClick={handleSave}
                  disabled={saving}
                  data-testid="settings-save-btn"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : saved ? (
                    <Check className="w-4 h-4 mr-1" />
                  ) : (
                    <Save className="w-4 h-4 mr-1" />
                  )}
                  {saved ? "Salvo!" : "Salvar"}
                </Button>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
      
      {/* Password Dialog para acessar Permissões */}
      <PasswordDialog
        open={showPasswordDialog}
        onClose={() => setShowPasswordDialog(false)}
        onSuccess={handlePasswordSuccess}
        title="Acesso às Permissões"
        description="Digite a senha de administrador para acessar as configurações de permissões"
      />
    </Dialog>
  );
}
